import React, { useCallback, useRef, useState, useEffect } from "react";
import {
	AppBar,
	Avatar,
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	CircularProgress,
	Grid2,
	IconButton,
	Toolbar,
	Typography,
	Dialog,
	DialogTitle,
	DialogContent,
} from "@mui/material";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCameraBackIcon from "@mui/icons-material/PhotoCameraBack";
import AgoraVideoCallWithSnapshot from "./AgoraVideoCallWithSnapshot";
import logo from "./Malayan.png";
import { useParams } from "react-router-dom";

import {
	fetchAssignmentsById,
	getDecryptedValue,
	analyseImage,
} from "./utility/api";
import { setAssignment } from "./features/AssignmentSlice";
import { useSelector, useDispatch } from "react-redux";
import { base64ToBlob, formatDisplayDate } from "./utility/utils";
import { uploadSnapshots } from "./utility/api";
import InfoField from "./component/InfoField";

function ClaimDetails() {
	const { token } = useParams(); // Extract assignmentName from URL
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [joined, setJoined] = useState(false);
	const videoRef = useRef(null);
	const canvasRef = useRef(null);
	const [isCameraOpen, setIsCameraOpen] = useState(false);
	const [showSubmit, setShowSubmit] = useState(false);
	const [isUploaded, setIsUploaded] = useState(false);
	const [snapshots, setSnapshots] = useState([]);
	const [gallery, setGallery] = useState([]);
	const dispatch = useDispatch();
	const [selectedImage, setSelectedImage] = useState(null);
	const [imageDialogOpen, setImageDialogOpen] = useState(false); // State to manage image dialog
	const currentAssignment = useSelector(
		(state) => state.assignment.assignment,
	);
	const user = useSelector((state) => state.user.user);
	const [stream, setStream] = useState(null);

	const handleJoinCall = () => {
		setJoined(true);
	};

	const fetchAssignmentDetails = useCallback(async () => {
		try {
			//console.log("token === ", token);
			const decryptedResponse = await getDecryptedValue(token);
			if (decryptedResponse.status === "INVALID") {
				console.error("Link expired");
				return;
			}

			const response = await fetchAssignmentsById(
				decryptedResponse.assignmentName,
			); // Fetch assignment details
			if (response === null) {
				alert("Assignment does not exist.");
			} else {
				dispatch(setAssignment(response));
				setIsUploaded(response.status === "InProgress" ? false : true);
			}
		} catch (error) {
			console.error("Error fetching assignment details:", error);
		}
	}, [dispatch, token]);

	useEffect(() => {
		fetchAssignmentDetails();
	}, [fetchAssignmentDetails]);

	const handleOpenCamera = () => {
		setIsCameraOpen(true);
		openCamera();
	};

	const fetchBlob = async (blobUrl) => {
		try {
			const response = await fetch(blobUrl);
			if (!response.ok) {
				throw new Error(`Failed to fetch blob: ${response.statusText}`);
			}
			const blob = await response.blob(); // Convert response to a Blob
			return blob;
		} catch (error) {
			console.error("Error fetching blob:", error);
		}
	};

	const handleSubmit = async () => {
		try {
			const d = new Date();
			let time = d.getTime();
			setIsSubmitting(true);
			const request = new FormData();
			const attachmentDetails = [];
			for (let index = 0; index < snapshots.length; index++) {
				const snapshot = snapshots[index];
				const fileName = `SnapShot-${
					currentAssignment.assignmentName
				}-${time}-${index + 1}.png`;
				const imageBlob = snapshots
					? await fetchBlob(snapshot.imageData)
					: base64ToBlob(snapshot.imageData, "image/png");
				request.append(`images${index}`, imageBlob, fileName);
				attachmentDetails.push({
					FileName: fileName,
					Height:
						snapshot.height === " " || isNaN(snapshot.height)
							? 0
							: snapshot.height,
					Width:
						snapshot.width === " " || isNaN(snapshot.width)
							? 0
							: snapshot.width,
					Description: snapshot.description,
					customDescription: snapshot.customDescription,
				});
			}

			// Append attachment details
			request.append(
				"attachmentDetails",
				JSON.stringify(attachmentDetails),
			);
			request.append("assignmentName", currentAssignment.assignmentName);

			// Upload snapshots and send message
			const updatedAssignmentList = await uploadSnapshots(request, user);
			setAssignment(updatedAssignmentList);
			closeCamera();
			setIsCameraOpen(false);
			setShowSubmit(false);
			setIsUploaded(true);
		} catch (error) {
			console.error("Error submitting images:", error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const openCamera = async () => {
		try {
			const cameraStream = await navigator.mediaDevices.getUserMedia({
				video: { facingMode: { exact: "environment" } }, // Use back camera
			});

			if (videoRef.current) {
				videoRef.current.srcObject = cameraStream;
				videoRef.current.style.transform = "scaleX(-1,-1)"; // Ensure no mirroring for back camera
			}

			setStream(cameraStream);
		} catch (error) {
			console.error("Error accessing the back camera:", error);

			try {
				const cameraStream = await navigator.mediaDevices.getUserMedia({
					video: true,
				}); // Fallback to default camera
				if (videoRef.current) {
					videoRef.current.srcObject = cameraStream;
					videoRef.current.style.transform = "scaleX(-1)"; // Apply mirroring for front camera
				}

				setStream(cameraStream);
			} catch (fallbackError) {
				console.error("Fallback camera access failed:", fallbackError);
			}
		}
	};

	const closeCamera = () => {
		if (stream) {
			// Stop all video tracks in the media stream
			stream.getTracks().forEach((track) => track.stop());
			setStream(null); // Clear the stream
		}
	};

	const handleImageClick = (image) => {
		setSelectedImage(image);
		setImageDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setImageDialogOpen(false);
	};

	const captureImage = async () => {
		if (canvasRef.current && videoRef.current) {
			const context = canvasRef.current.getContext("2d");
			canvasRef.current.width = videoRef.current.videoWidth;
			canvasRef.current.height = videoRef.current.videoHeight;

			// drawImage captures raw video data (unaffected by CSS transforms),
			// so no canvas translate/scale is needed.
			context.drawImage(
				videoRef.current,
				0,
				0,
				canvasRef.current.width,
				canvasRef.current.height,
			);

			const imageData = canvasRef.current.toDataURL("image/png");
			// const imageBlob = base64ToBlob(imageData);
			let width = 1,
				height = 1,
				description = "",
				customDescription = "";
			try {
				const analysisResult = await analyseImage(imageData);

				width = parseFloat(analysisResult.width);
				height = parseFloat(analysisResult.height);
				description = analysisResult.description;
				customDescription = analysisResult.customDescription || "";
				setSnapshots((prevSnapshots) => [
					...prevSnapshots,
					{
						imageData,
						customDescription,
						width,
						height,
						description,
					},
				]);
				setShowSubmit(true);
			} catch (error) {
				console.error("Error uploading data:", error);
			}
			setGallery((prevGallery) => [
				...prevGallery,
				{ imageData, description, customDescription },
			]);
			setShowSubmit(true);
		}
	};

	const handleDeleteSnapshot = (index) => {
		// setGallery(prevGallery => prevGallery.filter((_, i) => i !== index));
		setSnapshots((prevSnapshots) =>
			prevSnapshots.filter((_, i) => i !== index),
		);
		if (snapshots.length === 1) {
			setShowSubmit(false);
		}
	};

	return (
		<Box
			sx={{
				width: "100%",
				minHeight: "100vh",
				overflow: "auto",
				paddingBottom: 3,
			}}
		>
			<AppBar position="static">
				<Toolbar>
					<Avatar src={logo} />
					<Typography
						variant="h6"
						sx={{
							flexGrow: 1,
							fontSize: { xs: "1rem", sm: "1.5rem" },
						}}
					>
						Electronic Malayan Adjustment System (eMACS)
					</Typography>
				</Toolbar>
			</AppBar>

			<Card
				sx={{
					maxWidth: { xs: "100%", sm: 600 }, // Full width for mobile, limited for larger screens
					margin: "auto",
					mt: 4,
					padding: 2,
					background: "#f4f4f4",
					borderRadius: 2,
					boxShadow: 3,
				}}
			>
				<CardContent>
					<Typography
						variant="h5"
						gutterBottom
						sx={{
							fontSize: { xs: "1.2rem", sm: "1.5rem" },
							textAlign: "center",
						}}
					>
						{currentAssignment
							? currentAssignment.assignmentName
							: "Loading..."}
					</Typography>
					{currentAssignment &&
						renderAssignmentDetails(currentAssignment)}
					{isCameraOpen && (
						<Box sx={{ textAlign: "center", mt: 2 }}>
							<video
								ref={videoRef}
								autoPlay
								playsInline
								style={{
									width: "100%",
									height: "auto",
								}}
							/>
							<IconButton
								color="primary"
								onClick={captureImage}
								sx={{ mt: 2, fontSize: 24 }}
							>
								<PhotoCameraBackIcon />
							</IconButton>
							<canvas
								ref={canvasRef}
								style={{ display: "none" }}
							></canvas>
						</Box>
					)}
					<Box
						sx={{
							display: "flex",
							flexWrap: "wrap", // Allow images to wrap for smaller screens
							gap: 1,
							mt: 2,
							justifyContent: "center",
						}}
					>
						{gallery.map((snapshot, index) => (
							<Grid2 container spacing={2} key={index}>
								<Grid2 item size={12} sm={6}>
									<Box sx={{ position: "relative" }}>
										<img
											src={snapshot.imageData}
											alt={`Snapshot ${index + 1}`}
											style={{
												width: "100%",
												height: "auto",
												borderRadius: "5px",
												cursor: "pointer",
											}}
											onClick={() =>
												handleImageClick(
													snapshot.imageData,
												)
											}
										/>
										<IconButton
											sx={{
												position: "absolute",
												top: 0,
												right: 0,
												backgroundColor:
													"rgba(255, 255, 255, 0.7)",
												"&:hover": {
													backgroundColor:
														"rgba(255, 0, 0, 0.2)",
												},
											}}
											onClick={() =>
												handleDeleteSnapshot(index)
											}
										>
											<CloseIcon />
										</IconButton>
									</Box>
								</Grid2>
								<Grid2 item size={12} sm={6}>
									{snapshot.customDescription && (
										<Typography
											variant="subtitle2"
											sx={{ fontWeight: "bold", mb: 0.5 }}
										>
											Object Detected:{" "}
											{snapshot.customDescription}
										</Typography>
									)}
									<Typography>
										{snapshot.description}
									</Typography>
								</Grid2>
							</Grid2>
						))}
					</Box>
				</CardContent>
				<CardActions
					sx={{
						justifyContent: "center",
						flexWrap: "wrap", // Allow buttons to wrap for small screens
						gap: 1,
					}}
				>
					{!joined && !showSubmit && !isUploaded && (
						<Button
							startIcon={<VideoCallIcon />}
							onClick={handleJoinCall}
							variant="contained"
							color="primary"
							sx={{ minWidth: "100%" }} // Full-width buttons for mobile
						>
							Join Call
						</Button>
					)}
					{!joined && !showSubmit && !isUploaded && (
						<Button
							startIcon={<CameraAltIcon />}
							onClick={handleOpenCamera}
							variant="contained"
							color="primary"
							sx={{ minWidth: "100%" }}
						>
							Camera
						</Button>
					)}
					{showSubmit && (
						<Button
							onClick={handleSubmit}
							variant="contained"
							color="primary"
							sx={{ minWidth: "100%" }}
							disabled={isSubmitting}
							startIcon={
								isSubmitting && <CircularProgress size={20} />
							}
						>
							{isSubmitting ? "Submitting..." : "Submit"}
						</Button>
					)}
				</CardActions>
			</Card>
			{/* Video Call Dialog - portrait with floating self-view */}
			<Dialog
				open={joined}
				onClose={() => {}}
				maxWidth={false}
				PaperProps={{
					sx: {
						width: { xs: "100vw", sm: "min(440px, 95vw)" },
						height: { xs: "100dvh", sm: "min(720px, 90vh)" },
						m: 0,
						borderRadius: { xs: 0, sm: "16px" },
						overflow: "hidden",
						bgcolor: "#1a1a2e",
						p: 0,
						display: "flex",
					},
				}}
			>
				<AgoraVideoCallWithSnapshot
					onCallEnd={() => setJoined(false)}
					participantRole="Claimant"
				/>
			</Dialog>

			{/* Dialog for displaying selected image */}
			<Dialog
				open={imageDialogOpen}
				onClose={handleCloseDialog}
				maxWidth="lg"
			>
				<DialogTitle>
					Snapshot
					<IconButton
						aria-label="close"
						onClick={handleCloseDialog}
						style={{ position: "absolute", right: 8, top: 8 }}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent style={{ textAlign: "center" }}>
					{selectedImage && (
						<img
							src={selectedImage}
							alt="Selected Snapshot"
							style={{ width: "100%", height: "auto" }}
						/>
					)}
				</DialogContent>
			</Dialog>
		</Box>
	);
}

const renderAssignmentDetails = (assignment) => {
	if (!assignment) return null;

	return (
		<Box sx={{ p: 1 }}>
			<Typography
				variant="h6"
				sx={{
					mb: 2,
					color: "primary.main",
					fontWeight: "medium",
				}}
			>
				Basic Information
			</Typography>

			{/* Assignment Details */}
			<Box sx={{ mb: 2 }}>
				<Typography
					variant="subtitle2"
					sx={{
						mb: 2,
						color: "text.primary",
					}}
				>
					Assignment Details
				</Typography>
				<Grid2 container spacing={1}>
					<InfoField
						label="Claim Number"
						value={assignment.claimNumber}
						size={6}
					/>
					<InfoField
						label="Policy Number"
						value={assignment.policyNumber}
						size={6}
					/>
					<InfoField
						label="Insurance Company"
						value={assignment.insuranceCompany}
						size={12}
					/>
					<InfoField label="Type" value={assignment.type} size={12} />
					<InfoField label="Mode" value={assignment.mode} size={4} />
					<InfoField
						label="Settled Amount"
						value={assignment.settledAmount}
						size={4}
					/>
					<InfoField
						label="Nature of Loss"
						value={assignment.natureOfLoss}
						size={4}
					/>
				</Grid2>
			</Box>

			{/* Important Dates */}
			<Box sx={{ mb: 2 }}>
				<Typography
					variant="subtitle2"
					sx={{
						mb: 2,
						color: "text.primary",
					}}
				>
					Important Dates
				</Typography>
				<Grid2 container spacing={1}>
					<InfoField
						label="Date of Loss"
						value={formatDisplayDate(assignment.dateOfLoss)}
						size={12}
					/>
					<InfoField
						label="Assignment Received"
						value={formatDisplayDate(assignment.assignmentReceived)}
						size={12}
					/>
					<InfoField
						label="Date of Survey"
						value={formatDisplayDate(assignment.dateOfSurvey)}
						size={12}
					/>
				</Grid2>
			</Box>

			{/* Contact Information */}
			<Box sx={{ mb: 2 }}>
				<Typography
					variant="subtitle2"
					sx={{
						mb: 2,
						color: "text.primary",
					}}
				>
					Contact Information
				</Typography>
				<Grid2 container spacing={1}>
					{assignment.claimants.map((insuredInfo, index) => (
						<React.Fragment key={index}>
							<InfoField
								label={`${
									insuredInfo.isPrimary ? "Primary-" : ""
								} Insured Name`}
								value={insuredInfo.insuredName}
								size={6}
							/>
							<InfoField
								label={`${
									insuredInfo.isPrimary ? "Primary-" : ""
								} Insured Contact`}
								value={insuredInfo.insuredContactDetails}
								size={6}
							/>
						</React.Fragment>
					))}

					<InfoField
						label="Address"
						value={assignment.address}
						size={12}
						multiline
					/>
					<InfoField
						label="Broker/Agent Details"
						value={assignment.brokersOrInsurerInvolved}
						size={12}
					/>
				</Grid2>
			</Box>
		</Box>
	);
};

export default ClaimDetails;
